import { uniqBy } from "lodash";
import React, { PropsWithChildren } from "react";
import styled from "styled-components";

import { Text, VSpace } from "@examspark/common-ui";

import { Facet } from "../activities/activity-editor/hooks/useItemFacets";

import FilterCheckboxList from "./FilterCheckboxList";

const StyledFilter = styled(VSpace).attrs({ as: "section" })`
  gap: 1rem;

  h2 {
    font-size: 1.2em;
    margin: 0;
  }
`;

const FacetCheckboxFilter = (
  props: PropsWithChildren<{
    hide?: boolean;
    options: Facet[];
    selected: string[];
    onFilterChange?: (values: string[]) => void;
    propertyName: string;
    isLoading?: boolean;
    labelOverrides?: Record<string, string>;
    sortFn?: (options: Facet[]) => Facet[];
  }>,
) => {
  if (props.hide && props.selected.length === 0) return null;

  const getLabel = (facet: Facet) => {
    return props.labelOverrides?.[facet.value] ?? facet.label;
  };

  // Make sure that we always show selected facets, even if they don't exist in
  // the provided options list
  let options = uniqBy(
    props.options.concat(
      ...props.selected.map((value) => ({
        value,
        count: "0",
        label: value,
      })),
    ),
    (f) => f.value,
  );

  // Sort the options
  if (props.sortFn) {
    options = props.sortFn(options);
  } else {
    options = options.sort((a, b) =>
      getLabel(a).localeCompare(getLabel(b), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }

  if (!props.isLoading && options.length === 0 && !props.children) {
    return null;
  }

  return (
    <StyledFilter>
      <Text.H2>{props.propertyName}</Text.H2>
      {props.isLoading && <div>Loading...</div>}
      {!!props.children && props.children}
      {!props.isLoading && !!options.length && (
        <FilterCheckboxList>
          {options.map((value) => {
            return (
              <FilterCheckboxList.Item
                key={value.value}
                checked={props.selected.includes(value.value)}
                count={+value.count}
                onClick={() => {
                  props.onFilterChange?.(
                    props.selected.includes(value.value)
                      ? props.selected.filter((id) => id !== value.value)
                      : [...props.selected, value.value],
                  );
                }}
              >
                {getLabel(value)}
              </FilterCheckboxList.Item>
            );
          })}
        </FilterCheckboxList>
      )}
    </StyledFilter>
  );
};

export default FacetCheckboxFilter;
